import React, { useState, useCallback, useMemo } from "react";
import { Form, FloatingLabel } from "react-bootstrap";
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../account-management/AccountManagement.module.css';
import useAxios from "../../services/AxiosConfig";
import inputStyles from '../ManagementPages.module.css';
import { showAlert } from "../../components/PerinHealthAlertSlice";
import PerinHealthAlert from "../../components/PerinHealthAlert";
import { LeftArrowIcon } from "../../components/icons/Icons";
import Logger from "../../services/Logger";
import { LogElements, LogAction } from "../../services/LogElements";

const ForgotPassword = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const perinHealthAlert = useSelector((state) => state.perinHealthAlert);
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const httpService = useAxios(axiosConfig.envURL);
    
    const [page, setPage] = useState(0);
    const [validated, setValidated] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const [passInfo, setPassInfo] = useState({
        username: '',
        confirmationCode: '',
        password1: '',
        password2: ''
    });

    const pageConfig = useMemo(() => ({
        0: {
            title: "Forgot Password",
            message: "Please enter your email to get a verification code",
            buttonLabel: "Send",
            url: 'forgot-password',
            data: { username: passInfo.username },
            errorMessage: 'There was an error sending you the verification code, please verify your email is correct and try again'
        },
        1: {
            title: "Forgot Password",
            message: "Please enter the 6 digit code sent to your email",
            buttonLabel: "Verify"
        },
        2: {
            title: "Reset Password",
            message: "Enter a new password to reset your account",
            buttonLabel: "Save",
            url: 'forgot-password/confirmation',
            data: { 
                username: passInfo.username,  
                confirmationCode: passInfo.confirmationCode, 
                password: passInfo.password1
            },
            errorMessage: 'There was an error saving your new password, please try again'
        },
        3: {
            title: "Congratulations",
            message: "Password changed successfully",
            buttonLabel: "Continue to login",
            data: {
                username: passInfo.username,
                password: passInfo.password1
            }
        }
    }), [passInfo.username, passInfo.confirmationCode, passInfo.password1]);

    const getBackButtonText = useCallback(() => {
        if (page === 0) {
            return "Back to Login";
        } else {
            return "Back";
        }
    }, [page]);

    const handleBackButton = useCallback(() => {
        if (page === 0) {
            // If on email step, go back to login
            navigate('/');
        } else {
            // Otherwise, go back to previous step in the flow
            setPage(page - 1);
        }
    }, [page, navigate]);

    const nextPage = useCallback(() => {
        if (page === 3) {
            navigate('/');
        } else {
            setPage(page + 1);
        }
    }, [page, navigate]);

    const handleChange = useCallback((e) => {
        const { id, value } = e.target;
        setPassInfo(prevPassInfo => ({
            ...prevPassInfo, 
            [id]: value
        }));
    }, []);

    const showErrorAlert = useCallback((header, message) => {
        dispatch(showAlert({
            header,
            message,
            type: 'danger',
            dismissable: true
        }));
    }, [dispatch]);

    const handleEmailError = useCallback((error) => {
        const errorInfo = error.response?.data;
        
        if (errorInfo?.code === 500 || errorInfo?.code === 429) {
            if (errorInfo.message?.includes("Invalid email or password")) {
                showErrorAlert(
                    "Invalid Email", 
                    "The email address you entered is not valid. Please check your email and try again."
                );
            } else if (errorInfo.message?.includes("You have reached the maximum number of attempts")) {
                showErrorAlert(
                    "Error", 
                    errorInfo.message
                );
            } else {
                showErrorAlert(
                    "Error", 
                    "There was an error with forgot password. Please try again."
                );
            }
        } else {
            showErrorAlert(
                "Error", 
                "There was an error with forgot password. Please try again."
            );
        }
    }, [showErrorAlert]);

    const handlePasswordResetError = useCallback((error) => {
        const errorInfo = error.response?.data;
        
        if (errorInfo?.code === 500 && (
            errorInfo.message?.includes("CodeMismatchException") || 
            errorInfo.message?.includes("Invalid verification code")
        )) {
            showErrorAlert(
                "Invalid Code", 
                "The verification code you entered is incorrect. Please check your email and try again."
            );
        } else if (errorInfo?.code === 500 && errorInfo.message?.includes("Too many attempts")) {
            showErrorAlert(
                "Error", 
                errorInfo.message
            );
        } else if (errorInfo?.code === 500 && errorInfo.message?.includes("Verification code has expired")) {
            showErrorAlert(
                "Invalid Code", 
                "The verification code you entered has expired. Please request a new code."
            );
        } else if (errorInfo?.code === 500 && errorInfo.message?.includes("Password has previously been used")) {
            showErrorAlert(
                "Invalid Password", 
                "The password you entered has previously been used. Please enter a new password."
            );
        } else {
            showErrorAlert(
                "Error", 
                pageConfig[2].errorMessage
            );
        }
    }, [showErrorAlert, pageConfig]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        const form = e.currentTarget;

        if (form.checkValidity() === false) {
            e.stopPropagation();
            setValidated(true);
            return;
        }

        if (page === 2 && passInfo.password1 !== passInfo.password2) {
            setValidated(true);
            return;
        }

        setValidated(false);
        
        if (page === 1) {
            nextPage();
            return;
        }
        
        if (page === 3) {
            // Log successful password reset completion
            Logger.shared.info(
                LogElements.continueToLoginButton,
                LogAction.clicked,
                'ForgotPassword.js',
                'User Reset Password Successfully'
            );
            nextPage();
            return;
        }

        setIsLoading(true);
        
        try {
            const response = await httpService.request({
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                url: pageConfig[page].url,
                data: JSON.stringify(pageConfig[page].data),
            });
            
            if (response) {
                nextPage();
            }
        } catch (error) {
            console.error('Error:', error);
            
            if (page === 0) {
                handleEmailError(error);
            } else if (page === 2) {
                handlePasswordResetError(error);
            }
        } finally {
            setIsLoading(false);
        }
    }, [page, passInfo, pageConfig, httpService, handleEmailError, handlePasswordResetError, nextPage]);

    const handleResend = useCallback(async () => {
        setIsLoading(true);
        
        try {
            const response = await httpService.request({
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                url: pageConfig[0].url,
                data: JSON.stringify(pageConfig[0].data),
            });
            
            if (response) {
                showErrorAlert(
                    "Success", 
                    "Verification code has been resent to your email."
                );
            }
        } catch (error) {
            console.error('Error resending code:', error);
            handleEmailError(error);
        } finally {
            setIsLoading(false);
        }
    }, [pageConfig, httpService, handleEmailError, showErrorAlert]);

    const renderEmailInput = useCallback(() => (
        <Form.Group>
            <FloatingLabel label="Enter Email" className="mb-3">
                <Form.Control
                    id="username"
                    type="email"
                    value={passInfo.username}
                    style={{ width: "100%" }}
                    placeholder="Enter Email"
                    className={`${inputStyles.BorderedComponent}`}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                />
                <Form.Control.Feedback type="invalid">
                    Please provide a valid email.
                </Form.Control.Feedback>
            </FloatingLabel>
        </Form.Group>
    ), [passInfo.username, handleChange, isLoading]);

    const renderVerificationCodeInput = useCallback(() => (
        <Form.Group>
            <FloatingLabel label="Verification Code" className="mb-3">
                <Form.Control
                    id="confirmationCode"
                    value={passInfo.confirmationCode}
                    style={{ width: "100%" }}
                    placeholder="Verification Code"
                    className={`${inputStyles.BorderedComponent}`}
                    onChange={handleChange}
                    required
                    type="text"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    disabled={isLoading}
                    isInvalid={validated && (!passInfo.confirmationCode || passInfo.confirmationCode.length !== 6 || !/^\d{6}$/.test(passInfo.confirmationCode))}
                />
                <Form.Control.Feedback type="invalid">
                    Please enter a valid 6 digit confirmation code.
                </Form.Control.Feedback>
            </FloatingLabel>
        </Form.Group>
    ), [passInfo.confirmationCode, handleChange, isLoading, validated]);

    const renderPasswordInputs = useCallback(() => (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <Form.Group>
                <FloatingLabel label="New Password">
                    <Form.Control
                        id="password1"
                        value={passInfo.password1}
                        style={{ width: "100%" }}
                        placeholder="New Password"
                        className={`${inputStyles.BorderedComponent}`}
                        onChange={handleChange}
                        type={showPassword ? "text" : "password"}
                        required
                        disabled={isLoading}
                        pattern="^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$"
                        isInvalid={validated && (!passInfo.password1 || !/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/.test(passInfo.password1))}
                    />
                    <div 
                        className={inputStyles["styled-icon-container"]} 
                        onClick={() => setShowPassword((prev) => !prev)}
                        style={{ cursor: 'pointer' }}
                    >
                        {showPassword ? 
                            <FontAwesomeIcon icon="fa-regular fa-eye" className="eye-icon" size="lg" /> :
                            <FontAwesomeIcon icon="fa-regular fa-eye-slash" className="eye-icon" size="lg" />
                        }
                    </div>
                    <Form.Control.Feedback type="invalid">
                        Password must contain at least 8 characters, one uppercase letter, one lowercase letter, one number, and one special character (!@#$%^&*).
                    </Form.Control.Feedback>
                </FloatingLabel>
            </Form.Group>
            
            <Form.Group>
                <FloatingLabel label="Confirm New Password">
                    <Form.Control
                        id="password2"
                        value={passInfo.password2}
                        style={{ width: "100%" }}
                        placeholder="Confirm New Password"
                        className={`${inputStyles.BorderedComponent}`}
                        onChange={handleChange}
                        type={showPassword ? "text" : "password"}
                        required
                        disabled={isLoading}
                        isInvalid={validated && (!(passInfo.password2 === passInfo.password1) || !passInfo.password2)}
                    />
                    <div 
                        className={inputStyles["styled-icon-container"]} 
                        onClick={() => setShowPassword((prev) => !prev)}
                        style={{ cursor: 'pointer' }}
                    >
                        {showPassword ? 
                            <FontAwesomeIcon icon="fa-regular fa-eye" className="eye-icon" size="lg" /> :
                            <FontAwesomeIcon icon="fa-regular fa-eye-slash" className="eye-icon" size="lg" />
                        }
                    </div>
                    <Form.Control.Feedback type="invalid">
                        Passwords do not match.
                    </Form.Control.Feedback>
                </FloatingLabel>
            </Form.Group>
        </div>
    ), [passInfo.password1, passInfo.password2, handleChange, showPassword, isLoading, validated]);

    const renderResendButton = useCallback(() => (
        page === 1 && (
            <div
                style={{ 
                    backgroundColor: "white", 
                    color: "#00162E", 
                    fontSize: "16px", 
                    fontWeight: "700", 
                    alignSelf: "center", 
                    cursor: isLoading ? "not-allowed" : "pointer",
                    opacity: isLoading ? 0.6 : 1
                }}
                onClick={isLoading ? undefined : handleResend}
            >
                {isLoading ? "Sending..." : "Resend Code"}
            </div>
        )
    ), [page, isLoading, handleResend]);

    return (
        <div className='background-container'>
            <div className='main-container'>
                {perinHealthAlert.show && (
                    <PerinHealthAlert
                        show={perinHealthAlert.show}
                        type={perinHealthAlert.type}
                        header={perinHealthAlert.header}
                        message={perinHealthAlert.message}
                        dismissable={perinHealthAlert.dismissable}
                    />
                )}
                
                <div className="login-container" style={{gap: "0px !important"}}>
                    <button 
                        onClick={handleBackButton} 
                        className="activation-back-login-btn"
                        disabled={isLoading}
                    >
                        <LeftArrowIcon /> {getBackButtonText()}
                    </button>
                    
                    <h2 className="reg-title" style={{ color: "#00162E", fontSize: "24px", paddingTop:"50px" }}>
                        {pageConfig[page].title}
                    </h2>
                    
                    <Form 
                        onSubmit={handleSubmit} 
                        noValidate 
                        validated={validated} 
                        style={{ 
                            display: "flex", 
                            flexDirection: "column", 
                            align: "left", 
                            width: "300px", 
                            flex: "1", 
                            overflow: "auto" 
                        }}
                    >
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                            <p style={{ alignSelf: "center", color: "#373737" }}>
                                {pageConfig[page].message}
                            </p>
                            
                            {page === 0 && renderEmailInput()}
                            {page === 1 && renderVerificationCodeInput()}
                            {page === 2 && renderPasswordInputs()}
                            
                            <button 
                                className={styles.LoginButton}
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? "Processing..." : pageConfig[page].buttonLabel}
                            </button>
                            
                            {renderResendButton()}
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;